import { Injectable } from '@angular/core';
import { WarningStatusType } from '@dpr-details/enums';
import { DprForecastActivityGridData } from '@dpr-details/models/grid/dpr-forecast-activity-grid-data.model';
import { ProjectSetup } from '@shared/models';
import { DprForecastActivityCustomData } from '@shared/models/dpr';
import { ActivityMobilisation, CustomData } from '@shared/models/project-setup';
import { compareTime, convertTimeToDate, hasValue } from '@shared/utils';

@Injectable()
export class DprForecastActivityStatusService {
  getStatus(
    row: DprForecastActivityGridData,
    projectSetup: ProjectSetup
  ): WarningStatusType {
    if (!row) {
      return null;
    }

    if (
      !row.activityGroupId ||
      !row.activityId ||
      !row.locationId ||
      !row.start ||
      !row.finish ||
      !row.date
    ) {
      return WarningStatusType.RequiredFields;
    }

    if (this.compareTime(row.start, row.finish) === 1) {
      return WarningStatusType.ErrorInTime;
    }

    const customDataStatus = this.computeStatusCustomDataBased(
      row,
      projectSetup
    );

    if (customDataStatus != null) {
      return customDataStatus;
    }

    return null;
  }

  private computeStatusCustomDataBased(
    row: DprForecastActivityGridData,
    projectSetup: ProjectSetup
  ): WarningStatusType {
    const mandatoryActivityMobilisations =
      this.getMandatoryActivityMobilisations(
        row.activityId,
        projectSetup.activityMobilisationList
      );

    const completed = mandatoryActivityMobilisations.every((x) => {
      return this.isValueSetForMandatoryCustomData(
        row.dprForecastActivityCustomDataList,
        x.customDataId,
        projectSetup.customDataList
      );
    });

    return !completed ? WarningStatusType.RequiredCustomData : null;
  }

  private getMandatoryActivityMobilisations(
    activityId: number,
    activityMobilisations: ActivityMobilisation[]
  ): ActivityMobilisation[] {
    return activityMobilisations.filter(
      (a) =>
        a.activityId === activityId &&
        !a.parallelActivityType &&
        a.isActive &&
        a.isMandatory
    );
  }

  private isValueSetForMandatoryCustomData(
    dprActivityCustomDataList: DprForecastActivityCustomData[],
    customDataId: number,
    customDataList: CustomData[]
  ): boolean {
    const customData = customDataList?.find((x) => x.id === customDataId);

    return dprActivityCustomDataList.some(
      (x) =>
        x.customDataId === customDataId &&
        hasValue(x.value, customData?.dataTypeId)
    );
  }

  private compareTime(start: string, finish: string): number {
    return compareTime(convertTimeToDate(start), convertTimeToDate(finish));
  }
}
